import { Organization } from "@common/types/organization";
import { z } from "zod";
import { Form, FormikProvider, useFormik } from "formik";
import { FC, useRef } from "react";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { useAutofocus } from "@hooks/useAutofocus";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { useUpdateOrganizationMutation } from "@api/organization.api";

interface Props {
    organization: Organization;
    onClose(): void;
}

const updateNameFormSchema = z.object({
    name: z.string().min(1, "Firm name is required"),
});

type UpdateNameFormValues = z.infer<typeof updateNameFormSchema>;

const fields = createFormikFields<UpdateNameFormValues>();

export const UpdateNameForm: FC<Props> = ({ organization, onClose }) => {
    const { toast } = useToaster();
    const updateOrganizationMutation = useUpdateOrganizationMutation();
    const focusInputRef = useRef<HTMLInputElement>(null);
    useAutofocus(focusInputRef);

    const formik = useFormik<UpdateNameFormValues>({
        initialValues: {
            name: organization.name,
        },
        validate: validateFormikWithZodSchema(updateNameFormSchema),
        onSubmit: submitHelper({
            handler: async ({ name }) => {
                await updateOrganizationMutation.mutateAsync({
                    organizationId: organization.id,
                    payload: {
                        name,
                    },
                });

                toast("Firm name updated");
                onClose();
            },
        }),
    });

    return (
        <FormikProvider value={formik}>
            <VStack as={Form} gap={16}>
                <fields.Text
                    name="name"
                    label="Firm name"
                    ref={focusInputRef}
                    size="xl"
                />

                <HStack as="footer" justify="end">
                    <ButtonWithLoader
                        size="xl"
                        loading={formik.isSubmitting}
                        type="submit"
                        variant="primary"
                    >
                        Save
                    </ButtonWithLoader>
                </HStack>
            </VStack>
        </FormikProvider>
    );
};
